import { TranslateLoader } from '@ngx-translate/core';
import { catchError, map, Observable, of, shareReplay, timeout } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { EnvConfig, LocaleId } from './config';
import { getLocaleKey, TranslationMap, SupportedLocale } from './translate-utils';

/**
 * Custom translation loader for ngx-translate that:
 * - Loads translations from backend, with frontend bundles overriding stale backend keys
 * - Caches translations to prevent duplicate HTTP requests
 * - Provides callbacks to notify when backend translations complete loading
 * - Handles network timeouts and errors gracefully
 */
export class AppTranslateLoader implements TranslateLoader {
  /** Timeout for backend translation loading in milliseconds */
  private readonly BACKEND_LOAD_TIMEOUT = 5000;

  /** Track which languages have successfully loaded from backend */
  private static loadedLanguages = new Set<string>();

  /** Callbacks to execute when specific languages finish loading */
  private static languageCallbacks = new Map<string, Array<() => void>>();

  /** Cache translation observables to prevent duplicate HTTP calls */
  private static translationCache = new Map<string, Observable<any>>();

  constructor(private http: HttpClient, private env?: EnvConfig) {}

  /**
   * Register a callback to be called when a specific language finishes loading from backend.
   * If the language has already loaded, the callback is executed immediately.
   *
   * @param language - The language code to wait for (e.g., 'en', 'de', 'it')
   * @param callback - Function to call when language is loaded
   *
   * @example
   * ```typescript
   * AppTranslateLoader.onLanguageLoaded('en', () => {
   *   console.log('English translations are ready!');
   * });
   * ```
   */
  static onLanguageLoaded(language: string, callback: () => void): void {
    if (this.loadedLanguages.has(language)) {
      // Language already loaded, execute callback immediately
      callback();
    } else {
      // Register callback to be executed when language loads
      if (!this.languageCallbacks.has(language)) {
        this.languageCallbacks.set(language, []);
      }
      this.languageCallbacks.get(language)!.push(callback);
    }
  }

  /**
   * Notify all registered callbacks that a language has finished loading
   * @param language - The language code that finished loading
   */
  private notifyLanguageLoaded(language: string): void {
    AppTranslateLoader.loadedLanguages.add(language);

    const callbacks = AppTranslateLoader.languageCallbacks.get(language);
    if (callbacks) {
      callbacks.forEach((cb) => cb());
      AppTranslateLoader.languageCallbacks.delete(language);
    }
  }

  /**
   * Load translations for a specific language.
   * Uses caching to prevent duplicate HTTP requests.
   *
   * @param lang - Language code to load translations for
   * @returns Observable with backend translations overridden by frontend bundles
   */
  getTranslation(lang: string): Observable<any> {
    // Return cached observable if translation is already loading or loaded
    const cached = AppTranslateLoader.translationCache.get(lang);
    if (cached) {
      return cached;
    }

    const localeKey: LocaleId = this.env?.enforcedLocaleId ?? getLocaleKey(lang);

    // Get frontend fallback translations (always available immediately)
    const frontendTranslations: TranslationMap = SupportedLocale.get(localeKey)?.translation || {};

    // Load backend translations with timeout and fallback handling
    const backendTranslations$ = this.http.get(`languages/frontend/${lang}.json`).pipe(
      timeout(this.BACKEND_LOAD_TIMEOUT),
      map((backend) => {
        // Backend loaded successfully - frontend bundles win for conflicting keys
        this.notifyLanguageLoaded(lang);
        return { ...backend, ...frontendTranslations };
      }),
      catchError(() => {
        // Backend failed or timed out - use frontend translations only
        // Still notify that loading is "complete" to prevent infinite waiting
        this.notifyLanguageLoaded(lang);
        return of(frontendTranslations);
      }),
      // Share the result among all subscribers to prevent duplicate HTTP calls
      shareReplay(1),
    );

    // Cache the observable to prevent duplicate requests
    AppTranslateLoader.translationCache.set(lang, backendTranslations$);

    return backendTranslations$;
  }

  /**
   * Clear translation cache for a specific language or all languages.
   * Useful for forcing a reload of translations.
   *
   * @param language - Optional language code to clear. If not provided, clears all.
   */
  static clearCache(language?: string): void {
    if (language) {
      this.translationCache.delete(language);
      this.loadedLanguages.delete(language);
    } else {
      this.translationCache.clear();
      this.loadedLanguages.clear();
      this.languageCallbacks.clear();
    }
  }
}
